// OI-1411 / OI-1667: route REST calls through the `/api/v1` mount. Applied
// centrally in the axios request interceptor (src/services/rest.tsx) so every
// call — the generated OmcareRestClient and legacy direct-axios thunks alike —
// targets the versioned path without each call site knowing the scheme.

export const API_VERSION_PREFIX = '/api/v1';

// Kill switch (default OFF): uat/prod RestServers don't dual-mount /api/v1
// yet, and firebase-hosting-upper-env.yml can dispatch master straight to
// them — an ungated build would 404 every allowlisted call there. Flip the
// env flag per environment once its server ships the dual-mount.
export const apiV1Enabled = process.env.REACT_APP_ENABLE_API_V1 === 'true';

// First path segment of every resource the server dual-mounts under /api/v1
// (OmcareRestServer src/appFactory.js). An ALLOWLIST rather than an exception
// list on purpose: a path with no v1 twin (e.g. /ping, /root_image) stays on
// its legacy path — which the server still serves — instead of 404-ing.
// Over-versioning breaks; under-versioning is safe.
const VERSIONED_RESOURCES = new Set<string>([
  'auth',
  'register',
  'customer',
  'customers',
  'medications',
  'owners',
  'devices',
  'facility',
  'eng',
  'pharmacy',
  'pharmacy_tech',
  'pharmacies',
  'events',
  'factory',
  'reporting',
  'calls',
  'call-stats',
  'janus',
]);

/** Idempotently map a REST path (path only, no query string) to its `/api/v1` twin (or leave it as-is). */
export const apiVersionedPath = (path: string): string => {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (p === API_VERSION_PREFIX || p.startsWith(`${API_VERSION_PREFIX}/`)) {
    return p; // already versioned
  }
  if (p.startsWith('/v1/')) {
    // legacy /v1/<resource> alias -> /api/v1/<resource>, but only for
    // resources that actually have a v1 twin (e.g. /v1/pharmacies, /v1/events;
    // NOT the deferred /v1/env-switch, which has no twin yet).
    const aliasResource = p.split('/')[2] ?? '';
    return VERSIONED_RESOURCES.has(aliasResource) ? `/api${p}` : p;
  }
  const firstSegment = p.split('/')[1] ?? '';
  return VERSIONED_RESOURCES.has(firstSegment)
    ? `${API_VERSION_PREFIX}${p}`
    : p;
};

/**
 * Rewrite a full REST URL's path to its `/api/v1` twin. Only touches URLs on
 * our REST host (leaving chatbot / S3 / CDN untouched) and preserves query.
 */
export const apiVersionedUrl = (rawUrl: string, restHost: string): string => {
  try {
    const url = new URL(rawUrl);
    if (!restHost || url.host !== restHost) {
      return rawUrl; // unknown/other host — never rewrite (fail closed)
    }
    const versioned = apiVersionedPath(url.pathname);
    if (versioned === url.pathname) {
      return rawUrl;
    }
    url.pathname = versioned;
    return url.toString();
  } catch {
    return rawUrl; // relative / unparseable — leave untouched
  }
};

// Server persists these at login for the OI-1662 sunset floor; the 'web-admin'
// source keeps the centrally-deployed portal from pinning the mobile fleet floor.
export const APP_VERSION_HEADER = 'X-App-Version';
export const CLIENT_HEADER = 'X-Client';
export const CLIENT_NAME = 'web-admin';

/** Adds version-report headers to REST-host requests; other hosts untouched. */
export const applyVersionHeaders = (
  headers: Record<string, unknown>,
  rawUrl: string | undefined,
  restHost: string,
  version: string
): void => {
  if (!headers || !rawUrl || !version) return;
  try {
    const url = new URL(rawUrl);
    if (!restHost || url.host !== restHost) return;
  } catch {
    return;
  }
  headers[APP_VERSION_HEADER] = version;
  headers[CLIENT_HEADER] = CLIENT_NAME;
};
